import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { FaQuestionCircle, FaEye, FaChartLine, FaClock } from "react-icons/fa";
import {
    TrendingUp,
    Users,
    Layers,
    MessageSquare,
    ArrowUpRight,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    ExternalLink,
} from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";

const AQI_PRIMARY = "var(--navy)";
const AQI_SECONDARY = "#C2D7E2";

const normalizeExternalUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const getLinkLabel = (url) => {
    try {
        const parsedUrl = new URL(normalizeExternalUrl(url));
        return parsedUrl.hostname.replace(/^www\./, "");
    } catch {
        return url;
    }
};

// ── Stat Card ──────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, subtitle, icon: Icon, gradient, iconBg, onClick, isOpen }) => (
    <div
        className={`relative overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200${onClick ? " cursor-pointer hover:shadow-md" : ""} ${onClick && isOpen ? "border-navy/40 ring-1 ring-navy/20" : "border-gray-200"}`}
        onClick={onClick}
    >
        <div className="absolute inset-0" style={{ background: gradient }} />
        <div className="relative p-6">
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-600">{label}</p>
                    <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900">{value}</p>
                    <p className="mt-2 text-xs text-gray-500">{subtitle}</p>
                </div>
                <div className="flex flex-col items-end gap-2 ml-3 flex-shrink-0">
                    <div
                        className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm"
                        style={{ backgroundColor: iconBg }}
                    >
                        <Icon className="h-5 w-5" />
                    </div>
                    {onClick && (
                        <div className="text-gray-400">
                            {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
);

// ── Funding progress bar (per-campaign) ───────────────────────────────────────
const FundingBar = ({ label, raised, goal, pct }) => (
    <div className="mb-4 last:mb-0">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span className="font-medium truncate max-w-[55%]">{label}</span>
            <span>
                ${raised?.toLocaleString(undefined, { maximumFractionDigits: 0 })} /{" "}
                ${goal?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
        </div>
        <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                    width: `${Math.min(pct, 100)}%`,
                    backgroundColor: pct >= 100 ? "#16a34a" : AQI_PRIMARY,
                }}
            />
        </div>
        <p className="text-right text-xs text-gray-400 mt-0.5">{pct}% funded</p>
    </div>
);

// ── Campaign Updates Block ─────────────────────────────────────────────────
const CampaignUpdatesBlock = ({ campaignUpdates, renderMedia, onExpand }) => {
    if (!campaignUpdates || campaignUpdates.length === 0) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f0f6fa] mx-auto mb-3">
                    <FaChartLine className="text-navy text-xl" />
                </div>
                <p className="font-medium text-gray-700">No campaign updates yet</p>
                <p className="text-sm text-gray-400 mt-1">Updates will appear here as your campaign progresses.</p>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            {campaignUpdates.map((campaign, idx) => (
                <div key={idx} className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    {/* Campaign header strip */}
                    <div className="flex items-center justify-between px-6 py-4 bg-[#f0f6fa] border-b border-[#e3eaf3]">
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ backgroundColor: AQI_PRIMARY }}>
                                <FaChartLine className="text-white" style={{ fontSize: "11px" }} />
                            </div>
                            <span className="font-semibold text-sm tracking-wide truncate max-w-[200px]" style={{ color: AQI_PRIMARY }}>{campaign.name}</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-navy text-white flex-shrink-0">
                                {campaign.updates?.length ?? 0} update{(campaign.updates?.length ?? 0) !== 1 ? "s" : ""}
                            </span>
                        </div>
                        {campaign.updates?.length > 3 && (
                            <button
                                onClick={() => onExpand(idx)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold transition-all flex-shrink-0 bg-navy hover:bg-navy-2"
                            >
                                <FaEye className="h-3 w-3" />
                                View All {campaign.updates.length}
                            </button>
                        )}
                    </div>

                    {/* Update cards */}
                    <div className="p-5">
                        {!Array.isArray(campaign.updates) ? (
                            <p className="text-sm italic text-gray-500">{campaign.updates || "No updates yet."}</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {campaign.updates.slice(0, 3).map((update, i) => {
                                    const isObj = typeof update === "object" && update !== null;
                                    const timestamp =
                                        isObj && update.timestamp
                                            ? new Date(update.timestamp).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                            })
                                            : null;
                                    const title = isObj ? update.title : null;
                                    const text = isObj ? update.description || update.text : typeof update === "string" ? update : null;
                                    const url = isObj ? update.url : null;
                                    const media = isObj ? update.media : null;
                                    return (
                                        <div
                                            key={i}
                                            className="rounded-xl bg-[#F5F7FA] overflow-hidden"
                                            style={{ border: "1px solid #e3eaf3", borderLeft: `4px solid var(--navy)` }}
                                        >
                                            <div className="p-4">
                                                {timestamp && (
                                                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#e8f1f8] text-navy mb-3">
                                                        <FaClock className="h-2.5 w-2.5" />
                                                        {timestamp}
                                                    </span>
                                                )}
                                                {title && (
                                                    <p className="text-sm font-semibold text-navy leading-snug mb-2 break-words">{title}</p>
                                                )}
                                                {url && (
                                                    <a
                                                        href={normalizeExternalUrl(url)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="mb-2 inline-flex w-fit max-w-full items-center gap-1.5 rounded-full border border-navy/20 bg-[#f0f6fb] px-3 py-1 text-sm font-semibold text-navy transition hover:border-navy/40 hover:bg-[#e3eef8]"
                                                    >
                                                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                                        <span className="truncate">{getLinkLabel(url)}</span>
                                                    </a>
                                                )}
                                                {text && (
                                                    <p className="text-sm text-gray-700 leading-relaxed line-clamp-5">{text}</p>
                                                )}
                                                {media && (
                                                    <div className="mt-3 rounded-lg overflow-hidden border border-[#e3eaf3] bg-white">
                                                        {renderMedia(media)}
                                                    </div>
                                                )}
                                                {!title && !text && !url && !media && (
                                                    <p className="text-sm text-gray-400 italic">No content</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

const EntrepreneurDashboard = ({ user }) => {
    const navigate = useNavigate();
    const [clock, setClock] = useState(new Date());

    // ── Campaign check state ────────────────────────────────────────────────
    const [hasCampaigns, setHasCampaigns] = useState(null);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [saveProgress, setSaveProgress] = useState(false);
    const [campaignStatus, setCampaignStatus] = useState(null);
    const [applicationStatus, setApplicationStatus] = useState(null);
    const [issuerAgreement, setIssuerAgreement] = useState(null);
    const [loadingIssuerAgreement, setLoadingIssuerAgreement] = useState(true);
    const [issuerSignature, setIssuerSignature] = useState("");
    const [issuerConsent, setIssuerConsent] = useState(false);
    const [signingIssuerAgreement, setSigningIssuerAgreement] = useState(false);
    const [issuerAgreementError, setIssuerAgreementError] = useState("");

    // ── Dashboard stats ─────────────────────────────────────────────────────
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    // ── Campaign updates (existing feature) ────────────────────────────────
    const [campaignUpdates, setCampaignUpdates] = useState([]);
    const [expandedCampaignIndex, setExpandedCampaignIndex] = useState(null);
    // true when campaigns endpoint shows any investment record (pending counts too)
    const [campaignsHaveActivity, setCampaignsHaveActivity] = useState(false);

    // ── Campaign questions (existing feature) ──────────────────────────────
    const [campaignQuestions, setCampaignQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(true);

    // ── Q&A panel toggle (on Pending Questions stat card click) ─────────────
    const [showQuestionsPanel, setShowQuestionsPanel] = useState(false);

    // Utility function to detect media file type
    const getMediaType = (fileUrl) => {
        if (!fileUrl) return "unknown";
        if (/\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl)) return "image";
        if (/\.mp4$/i.test(fileUrl)) return "video";
        if (/\.pdf$/i.test(fileUrl)) return "pdf";
        return "unknown";
    };

    const normalizeAssetUrl = (url) => {
        if (!url || typeof url !== "string") return "";
        if (/^https?:\/\//i.test(url)) return url;
        return `${process.env.REACT_APP_BACKEND_URL}/uploads/${url.replace(/^\/?uploads\//, "")}`;
    };

    // Function to render media content based on type
    const renderMedia = (mediaUrl) => {
        const normalizedMediaUrl = normalizeAssetUrl(mediaUrl);
        const mediaType = getMediaType(normalizedMediaUrl);

        switch (mediaType) {
            case "image":
                return (
                    <img
                        src={normalizedMediaUrl}
                        alt="Update media"
                        className="max-h-48 max-w-full object-contain rounded"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                        }}
                    />
                );
            case "video":
                return (
                    <video
                        src={normalizedMediaUrl}
                        controls
                        className="max-h-48 max-w-full rounded"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                        }}
                    />
                );
            case "pdf":
                return (
                    <iframe
                        src={normalizedMediaUrl}
                        title="PDF document"
                        width="100%"
                        height="150"
                        className="border rounded"
                    />
                );
            default:
                return (
                    <div className="text-red-600 font-semibold">
                        Unsupported file type
                    </div>
                );
        }
    };

    useEffect(() => {
        const interval = setInterval(() => setClock(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const fetchCampaignStatus = async () => {
            try {
                const userId = user?.id;

                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/${userId}/has-campaigns`,
                    {
                        withCredentials: true,
                    },
                );

                // Check both hasCampaigns and submitted status
                // If campaigns exist but are not submitted, we should still show the "No Campaigns" message
                const hasCampaignsSubmitted =
                    response.data.hasCampaigns && response.data.submitted;

                setHasCampaigns(hasCampaignsSubmitted);

                // Store the campaign status
                setCampaignStatus(response.data.status);

                // Store the save progress flag
                setSaveProgress(response.data.saveProgress);

                // Store the application status
                setApplicationStatus(response.data.applicationStatus);
            } catch (err) {
                console.error("Error checking campaign status:", err);
                setHasCampaigns(false);
                setCampaignStatus(null);
                setSaveProgress(false);
                setApplicationStatus(null);
            } finally {
                setLoadingCampaigns(false);
            }
        };

        fetchCampaignStatus();
    }, [user]);

    useEffect(() => {
        const fetchIssuerAgreement = async () => {
            if (!user?.id) {
                setLoadingIssuerAgreement(false);
                return;
            }

            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/issuer-agreement`,
                    { withCredentials: true },
                );
                setIssuerAgreement(response.data);
                if (!response.data.accepted) {
                    setIssuerSignature(
                        `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                    );
                }
            } catch (error) {
                setIssuerAgreementError(
                    error.response?.data?.error ||
                        "Unable to load the Issuer User Agreement.",
                );
            } finally {
                setLoadingIssuerAgreement(false);
            }
        };

        fetchIssuerAgreement();
    }, [user]);

    const handleIssuerAgreementAcceptance = async () => {
        setSigningIssuerAgreement(true);
        setIssuerAgreementError("");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/issuer-agreement/accept`,
                {
                    signatureText: issuerSignature,
                    consentAccepted: issuerConsent,
                },
                { withCredentials: true },
            );
            setIssuerAgreement(response.data);
            setIssuerConsent(false);
        } catch (error) {
            setIssuerAgreementError(
                error.response?.data?.error ||
                    "Unable to sign the Issuer User Agreement.",
            );
        } finally {
            setSigningIssuerAgreement(false);
        }
    };

    // Fetch campaign updates
    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const userId = user?.id;

                if (!userId) return;
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/accounts/entrepreneur/${userId}/campaigns`;

                const res = await fetch(url, {
                    credentials: "include",
                });
                const data = await res.json();

                if (Array.isArray(data)) {
                    // Detect if any campaign has investment records (pending counts too)
                    const anyInvestments = data.some(
                        (c) => c.has_any_investments === true || (c.raised || 0) > 0,
                    );
                    setCampaignsHaveActivity(anyInvestments);

                    setCampaignUpdates(
                        data.map((c) => {
                            let updates = c.updates;

                            // Try to parse updates if it's a stringified array
                            if (typeof updates === "string") {
                                try {
                                    const parsed = JSON.parse(updates);
                                    if (Array.isArray(parsed)) {
                                        updates = parsed;

                                    }
                                } catch {
                                    console.log(
                                        "Failed to parse updates for campaign",
                                        c.name,
                                        updates,
                                    );
                                }
                            }
                            if (Array.isArray(updates)) {
                                updates = updates.map((update) => {
                                    if (typeof update === "string") {
                                        return {
                                            text: update,
                                            media: null,
                                            timestamp: new Date().toISOString(),
                                        };
                                    }
                                    if (
                                        typeof update === "object" &&
                                        update !== null
                                    ) {
                                        return {
                                            title: update.title || "",
                                            description:
                                                update.description || "",
                                            text:
                                                update.text ||
                                                update.description ||
                                                "",
                                            url: update.url || "",
                                            media: update.media || null,
                                            timestamp:
                                                update.timestamp ||
                                                new Date().toISOString(),
                                        };
                                    }
                                    return update;
                                });
                            }
                            return {
                                name: c.name,
                                updates,
                            };
                        }),
                    );
                }
            } catch (err) {
                console.error(
                    "EntrepreneurDashboard: Error fetching campaigns",
                    err,
                );
                setCampaignUpdates([]);
            }
        };
        fetchCampaigns();
    }, [user]);

    // Fetch campaign questions for the entrepreneur
    useEffect(() => {
        const fetchCampaignQuestions = async () => {
            try {
                const userId = user?.id;

                if (!userId) return;

                setLoadingQuestions(true);
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/${userId}/questions`;

                const res = await fetch(url, {
                    credentials: "include",
                });
                const data = await res.json();

                setCampaignQuestions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching campaign questions:", err);
                setCampaignQuestions([]);
            } finally {
                setLoadingQuestions(false);
            }
        };

        fetchCampaignQuestions();
    }, [user]);

    // Fetch dashboard stats
    useEffect(() => {
        const fetchStats = async () => {
            const userId = user?.id;
            if (!userId) {
                setLoadingStats(false);
                return;
            }
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/${userId}/stats`,
                    { withCredentials: true },
                );
                setStats(res.data);
            } catch {
                setStats(null);
                console.error("[EntrepreneurDashboard] Stats API failed - check backend");
            } finally {
                setLoadingStats(false);
            }
        };
        fetchStats();
    }, [user]);

    // ── Derived ──────────────────────────────────────────────────────────────
    // Show full stats if ANY investment record exists (pending counts too)
    const hasInvestments =
        !!stats?.has_any_investments ||
        (stats?.total_investors ?? 0) > 0 ||
        (stats?.recent_investors?.length ?? 0) > 0 ||
        campaignsHaveActivity;  // fallback from campaigns endpoint (independent, always works)

    // ── Status badge helper ───────────────────────────────────────────────────
    const statusBadge = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "approved")
            return "bg-navy text-white ring-navy";
        if (s === "pending")
            return "bg-[#C2D7E2] text-navy ring-[#C2D7E2]";
        if (s === "rejected" || s === "cancelled")
            return "bg-red-100 text-red-700 ring-red-200";
        return "bg-gray-100 text-gray-600 ring-gray-200";
    };

    return (
        <div className="min-h-screen bg-white p-6 font-poppins">

            {/* ── Welcome Section ───────────────────────────────────────────── */}
            <div className="mb-8">
                <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div
                        className="absolute inset-0"
                        style={{ background: `linear-gradient(135deg, #C2D7E255 0%, #ffffff 55%)` }}
                    />
                    <div className="relative flex flex-col gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                                Welcome,{" "}
                                <span style={{ color: AQI_PRIMARY }}>
                                    {user?.first_name || "Entrepreneur"}
                                </span>!
                            </h1>
                            <p className="mt-1 text-sm text-gray-500">
                                <span className="font-medium text-gray-700">{user?.email}</span>
                                <span className="mx-2 text-gray-300">•</span>
                                <span>{clock.toLocaleString()}</span>
                            </p>
                            <div className="mt-3">
                                <span
                                    className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1"
                                    style={{
                                        backgroundColor: "#C2D7E2",
                                        color: AQI_PRIMARY,
                                        borderColor: "#C2D7E2",
                                    }}
                                >
                                    {user?.role?.toUpperCase() || "ENTREPRENEUR"}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            {hasCampaigns === true && (
                                <button
                                    onClick={() => navigate("/campaign-summary")}
                                    className="px-4 py-2 text-white text-sm font-semibold rounded-lg shadow flex items-center gap-2 transition-all bg-navy hover:bg-navy-2"
                                >
                                    <FaEye className="h-3.5 w-3.5" />
                                    View Campaigns
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Loading spinner ───────────────────────────────────────────── */}
            {(loadingCampaigns || loadingStats) && (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy" />
                </div>
            )}

            {/* ── Account not approved ──────────────────────────────────────── */}
            {!loadingCampaigns &&
                hasCampaigns === false &&
                applicationStatus &&
                applicationStatus !== "approved" && (
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-center">
                        <div className="flex flex-col items-center justify-center py-4">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 mb-4">
                                <FaClock className="h-8 w-8 text-blue-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Account Under Review</h3>
                            <p className="text-gray-500 max-w-md mb-6">
                                Your account is currently{" "}
                                <span className="font-semibold text-gray-700">{applicationStatus}</span>.
                                Once approved, you will be able to create and manage campaigns.
                            </p>
                        </div>
                    </div>
                )}

            {/* Issuer User Agreement gate */}
            {!loadingCampaigns &&
                !loadingIssuerAgreement &&
                applicationStatus === "approved" &&
                issuerAgreement?.required &&
                !issuerAgreement.accepted && (
                    <div className="overflow-hidden rounded-2xl border border-[#D4AF37]/50 bg-white shadow-sm">
                        <div className="border-b border-gray-200 bg-[#f8f5eb] px-6 py-5 sm:px-8">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy text-white">
                                    <CheckCircle className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#9a7a16]">
                                        Action required
                                    </p>
                                    <h2 className="mt-1 text-xl font-bold text-gray-900">
                                        Sign the Issuer User Agreement
                                    </h2>
                                    <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">
                                        Your application is approved. Before you can build your campaign,
                                        review and electronically sign the agreement between you and AQi.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-5 p-6 sm:p-8">
                            {issuerAgreement.configured ? (
                                <>
                                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                                        <iframe
                                            src={issuerAgreement.agreementUrl}
                                            title={issuerAgreement.agreementName}
                                            className="h-[420px] w-full bg-white"
                                        />
                                    </div>
                                    <a
                                        href={issuerAgreement.agreementUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 text-sm font-semibold text-navy underline"
                                    >
                                        Open {issuerAgreement.agreementName} in a new tab
                                        <ExternalLink className="h-4 w-4" />
                                    </a>

                                    <div className="rounded-xl border border-gray-200 bg-[#F8FAFC] p-5">
                                        <label
                                            htmlFor="issuer-signature"
                                            className="block text-sm font-semibold text-gray-800"
                                        >
                                            Electronic signature — enter your full legal name
                                        </label>
                                        <input
                                            id="issuer-signature"
                                            type="text"
                                            value={issuerSignature}
                                            onChange={(event) =>
                                                setIssuerSignature(event.target.value)
                                            }
                                            maxLength={255}
                                            className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                                        />

                                        <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm leading-6 text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={issuerConsent}
                                                onChange={(event) =>
                                                    setIssuerConsent(event.target.checked)
                                                }
                                                className="mt-1 h-4 w-4 rounded border-gray-300 text-navy focus:ring-navy"
                                            />
                                            <span>
                                                I have reviewed the Issuer User Agreement and agree
                                                to sign it electronically.
                                            </span>
                                        </label>

                                        {issuerAgreementError && (
                                            <p className="mt-4 text-sm font-medium text-red-600">
                                                {issuerAgreementError}
                                            </p>
                                        )}

                                        <button
                                            type="button"
                                            onClick={handleIssuerAgreementAcceptance}
                                            disabled={
                                                signingIssuerAgreement ||
                                                !issuerConsent ||
                                                issuerSignature.trim().length < 2
                                            }
                                            className="mt-5 inline-flex items-center justify-center rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white transition hover:bg-navy-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {signingIssuerAgreement
                                                ? "Recording signature..."
                                                : "Agree and sign electronically"}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                                    The Issuer User Agreement is not currently available. Please
                                    contact your Client Success Manager or AQi support before
                                    continuing.
                                </div>
                            )}
                        </div>
                    </div>
                )}

            {!loadingIssuerAgreement &&
                applicationStatus === "approved" &&
                issuerAgreementError &&
                !issuerAgreement && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm font-medium text-red-700">
                        {issuerAgreementError}
                    </div>
                )}

            {/* ── No campaigns yet (approved, no campaign) ─────────────────── */}
            {!loadingCampaigns &&
                !loadingIssuerAgreement &&
                hasCampaigns === false &&
                applicationStatus === "approved" &&
                issuerAgreement?.accepted === true && (
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-center">
                        <div className="flex flex-col items-center justify-center py-4">
                            <div
                                className="flex h-16 w-16 items-center justify-center rounded-full mb-4"
                                style={{ backgroundColor: "#C2D7E2" }}
                            >
                                <FaChartLine className="h-8 w-8" style={{ color: AQI_PRIMARY }} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">No Campaigns Yet</h3>
                            <p className="text-gray-500 max-w-md mb-6">
                                {saveProgress
                                    ? "You have a campaign in progress. Continue working on it and submit when ready!"
                                    : "You haven't created any campaigns yet. Start your fundraising journey today!"}
                            </p>
                            <a
                                href="/campaign-page"
                                className="inline-flex items-center px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors duration-200"
                                style={{ backgroundColor: saveProgress ? "#D4AF37" : AQI_PRIMARY }}
                            >
                                {saveProgress ? "Continue Campaign" : "Create Campaign"}
                            </a>
                        </div>
                    </div>
                )}

            {/* ── Campaign pending approval ──────────────────────────────────── */}
            {!loadingCampaigns &&
                hasCampaigns === true &&
                campaignStatus !== "approved" && (
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-center">
                        <div className="flex flex-col items-center justify-center py-4">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 mb-4">
                                <FaClock className="h-8 w-8 text-amber-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                Campaign {campaignStatus || "Pending"}
                            </h3>
                            <p className="text-gray-500 max-w-md mb-6">
                                Your campaign is currently{" "}
                                {campaignStatus === "pending" ? "under review" : campaignStatus}.
                                You'll be notified once it's approved.
                            </p>
                            <button
                                onClick={() => navigate("/Campaign-tracker")}
                                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all bg-navy hover:bg-navy-2"
                            >
                                <FaEye className="h-3.5 w-3.5" />
                                View Campaign Details
                            </button>
                        </div>
                    </div>
                )}

            {/* ── Main dashboard (approved + has campaigns) ─────────────────── */}
            {!loadingCampaigns &&
                !loadingStats &&
                hasCampaigns === true &&
                campaignStatus === "approved" && (
                    <section className="space-y-6">

                        {/* ═══════════════════════════════════════════════════════
                             HAS INVESTMENTS — full stats view
                        ═══════════════════════════════════════════════════════ */}
                        {hasInvestments && (
                            <>
                                {/* 4 stat cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                                    <StatCard
                                        label="Total Raised"
                                        value={`$${(stats?.total_raised || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                        subtitle="Across all campaigns"
                                        icon={TrendingUp}
                                        gradient="linear-gradient(135deg, #5B8FB355 0%, #ffffff 60%)"
                                        iconBg="#2F6F95"
                                    />
                                    <StatCard
                                        label="Total Investors"
                                        value={stats?.total_investors ?? 0}
                                        subtitle="Unique investors"
                                        icon={Users}
                                        gradient={`linear-gradient(135deg, ${AQI_SECONDARY}55 0%, #ffffff 60%)`}
                                        iconBg={AQI_PRIMARY}
                                    />
                                    <StatCard
                                        label="Active Campaigns"
                                        value={stats?.active_campaigns ?? 0}
                                        subtitle={`of ${stats?.total_campaigns ?? 0} total campaigns`}
                                        icon={Layers}
                                        gradient="linear-gradient(135deg, #DCFCE755 0%, #ffffff 60%)"
                                        iconBg="#16a34a"
                                    />
                                    <StatCard
                                        label="Pending Questions"
                                        value={stats?.pending_questions ?? 0}
                                        subtitle={`${stats?.total_questions ?? 0} questions total · click to view`}
                                        icon={MessageSquare}
                                        gradient={
                                            (stats?.pending_questions ?? 0) > 0
                                                ? "linear-gradient(135deg, #FEF3C755 0%, #ffffff 60%)"
                                                : "linear-gradient(135deg, #F5F7FA 0%, #ffffff 60%)"
                                        }
                                        iconBg={(stats?.pending_questions ?? 0) > 0 ? "#EAB308" : "#9CA3AF"}
                                        onClick={() => setShowQuestionsPanel((v) => !v)}
                                        isOpen={showQuestionsPanel}
                                    />
                                </div>

                                {/* Inline Q&A panel — shown when Pending Questions card is clicked */}
                                {showQuestionsPanel && (
                                    <div className="rounded-2xl border border-navy/20 bg-white shadow-sm overflow-hidden">
                                        {/* Panel header */}
                                        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e3eaf3]" style={{ background: "linear-gradient(90deg, #f0f6fa 0%, #ffffff 100%)" }}>
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-9 w-9 items-center justify-center rounded-xl text-white" style={{ backgroundColor: AQI_PRIMARY }}>
                                                    <FaQuestionCircle className="h-4 w-4" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-gray-900">Investor Questions</h3>
                                                    <p className="text-xs text-gray-500">{campaignQuestions.length} total · {campaignQuestions.filter((q) => !q.answer).length} pending</p>
                                                </div>
                                                {campaignQuestions.filter((q) => !q.answer).length > 0 && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                                                        {campaignQuestions.filter((q) => !q.answer).length} unanswered
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => navigate("/entrepreneur-questions")}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all bg-navy hover:bg-navy-2"
                                            >
                                                View All
                                                <ArrowUpRight className="h-3.5 w-3.5" />
                                            </button>
                                        </div>

                                        {loadingQuestions ? (
                                            <div className="flex items-center justify-center h-24">
                                                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-navy" />
                                            </div>
                                        ) : campaignQuestions.length === 0 ? (
                                            <div className="flex items-center gap-3 px-6 py-6">
                                                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                                <p className="text-sm text-gray-500">No investor questions yet.</p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-100">
                                                {campaignQuestions.slice(0, 6).map((q, qi) => (
                                                    <div key={qi} className="px-6 py-4 hover:bg-gray-50/60 transition-colors">
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e8f1f8] flex-shrink-0 mt-0.5">
                                                                <span className="text-xs font-bold" style={{ color: AQI_PRIMARY }}>
                                                                    {(q.asker_first_name || "?")[0].toUpperCase()}
                                                                </span>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                                    <span className="text-xs font-semibold text-gray-700">
                                                                        {q.asker_first_name} {q.asker_last_name}
                                                                    </span>
                                                                    {q.campaign_name && (
                                                                        <span className="text-xs text-gray-400">· {q.campaign_name}</span>
                                                                    )}
                                                                    {!q.answer ? (
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Pending</span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Answered</span>
                                                                    )}
                                                                </div>
                                                                <p className="text-sm font-medium text-gray-900 mb-1">{q.question}</p>
                                                                {q.answer && (
                                                                    <p className="text-sm text-gray-500 pl-3 border-l-2 border-[#C2D7E2] leading-relaxed">
                                                                        {q.answer}
                                                                    </p>
                                                                )}
                                                                {!q.answer && (
                                                                    <button
                                                                        onClick={() => navigate("/entrepreneur-questions")}
                                                                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                                                                        style={{ color: AQI_PRIMARY }}
                                                                    >
                                                                        Answer this question
                                                                        <ArrowUpRight className="h-3 w-3" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {campaignQuestions.length > 6 && (
                                                    <div className="px-6 py-3 text-center">
                                                        <button
                                                            onClick={() => navigate("/entrepreneur-questions")}
                                                            className="text-sm font-semibold hover:underline"
                                                            style={{ color: AQI_PRIMARY }}
                                                        >
                                                            View all {campaignQuestions.length} questions →
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Charts row */}
                                {stats?.campaign_stats?.length > 0 && (
                                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                                            <div className="px-6 pt-6">
                                                <h2 className="text-base font-semibold" style={{ color: AQI_PRIMARY }}>Campaign Funding Progress</h2>
                                                <p className="mt-1 text-sm text-gray-500">Raised vs goal per campaign</p>
                                            </div>
                                            <div className="px-6 pb-6 pt-4">
                                                <ResponsiveContainer width="100%" height={280}>
                                                    <BarChart
                                                        data={stats.campaign_stats.map((c) => ({
                                                            name: c.campaign_name?.length > 14 ? c.campaign_name.slice(0, 14) + "…" : c.campaign_name,
                                                            Raised: parseFloat(c.raised || 0),
                                                            Goal: parseFloat(c.goal || 0),
                                                        }))}
                                                        barSize={22}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#4B5563" }} />
                                                        <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                                        <Tooltip
                                                            contentStyle={{ borderRadius: "10px", border: "1px solid #E5E7EB", fontSize: "13px" }}
                                                            formatter={(value) => `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                                                        />
                                                        <Legend />
                                                        <Bar dataKey="Raised" fill={AQI_PRIMARY} radius={[6, 6, 0, 0]} />
                                                        <Bar dataKey="Goal" fill={AQI_SECONDARY} radius={[6, 6, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                                            <div className="px-6 pt-6">
                                                <h2 className="text-base font-semibold" style={{ color: AQI_PRIMARY }}>Campaign Overview</h2>
                                                <p className="mt-1 text-sm text-gray-500">Funding progress per campaign</p>
                                            </div>
                                            <div className="px-6 pb-6 pt-4">
                                                {stats.campaign_stats.map((c) => (
                                                    <FundingBar key={c.id} label={c.campaign_name} raised={c.raised} goal={c.goal} pct={c.percentage_raised} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Recent Investors */}
                                {stats?.recent_investors?.length > 0 && (
                                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                                        <div className="flex items-center justify-between px-6 pt-6">
                                            <div>
                                                <h2 className="text-base font-semibold" style={{ color: AQI_PRIMARY }}>Recent Investors</h2>
                                                <p className="mt-1 text-sm text-gray-500">Latest investments in your campaigns</p>
                                            </div>
                                            <button
                                                className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity"
                                                style={{ color: AQI_PRIMARY }}
                                                onClick={() => navigate("/campaign-summary")}
                                            >
                                                View all <ArrowUpRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <div className="px-6 pb-6 pt-4 overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                                <thead>
                                                    <tr className="text-left text-gray-500">
                                                        <th className="py-3 pr-4 font-medium">Date</th>
                                                        <th className="py-3 pr-4 font-medium">Investor</th>
                                                        <th className="py-3 pr-4 font-medium text-right">Amount</th>
                                                        <th className="py-3 pr-4 font-medium">Campaign</th>
                                                        <th className="py-3 pr-4 font-medium">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {stats.recent_investors.map((inv, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50/60">
                                                            <td className="py-3 pr-4 text-gray-700">{inv.date ? new Date(inv.date).toLocaleDateString() : "—"}</td>
                                                            <td className="py-3 pr-4 font-medium text-gray-900">{inv.investor_name}</td>
                                                            <td className="py-3 pr-4 text-right font-semibold text-gray-900">
                                                                ${inv.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="py-3 pr-4 text-gray-700">{inv.campaign_name}</td>
                                                            <td className="py-3 pr-4">
                                                                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusBadge(inv.status)}`}>
                                                                    {inv.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Campaign Updates */}
                                <CampaignUpdatesBlock
                                    campaignUpdates={campaignUpdates}
                                    renderMedia={renderMedia}
                                    onExpand={setExpandedCampaignIndex}
                                />
                            </>
                        )}

                        {/* ═══════════════════════════════════════════════════════
                             NO INVESTMENTS — only questions + updates
                        ═══════════════════════════════════════════════════════ */}
                        {!hasInvestments && (
                            <>
                                {/* Campaign Questions — full section */}
                                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                                    <div className="flex items-center justify-between px-6 py-5 border-b border-[#e3eaf3]" style={{ background: "linear-gradient(90deg, #f0f6fa 0%, #ffffff 100%)" }}>
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ backgroundColor: AQI_PRIMARY }}>
                                                <FaQuestionCircle className="h-4.5 w-4.5" />
                                            </div>
                                            <div>
                                                <h2 className="text-base font-semibold text-gray-900">Campaign Questions</h2>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {campaignQuestions.length} total · {campaignQuestions.filter((q) => !q.answer).length} awaiting your reply
                                                </p>
                                            </div>
                                            {campaignQuestions.filter((q) => !q.answer).length > 0 && (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                                                    {campaignQuestions.filter((q) => !q.answer).length} unanswered
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => navigate("/entrepreneur-questions")}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all bg-navy hover:bg-navy-2"
                                        >
                                            <FaEye className="h-3.5 w-3.5" />
                                            View All
                                        </button>
                                    </div>

                                    {loadingQuestions ? (
                                        <div className="flex items-center justify-center h-24">
                                            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-navy" />
                                        </div>
                                    ) : campaignQuestions.length === 0 ? (
                                        <div className="flex items-center gap-3 px-6 py-8 text-center justify-center">
                                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                            <p className="text-sm text-gray-500">No investor questions yet. They'll appear here once investors start asking.</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {campaignQuestions.slice(0, 8).map((q, qi) => (
                                                <div key={qi} className="px-6 py-4 hover:bg-gray-50/60 transition-colors">
                                                    <div className="flex items-start gap-3">
                                                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e8f1f8] flex-shrink-0 mt-0.5">
                                                            <span className="text-sm font-bold" style={{ color: AQI_PRIMARY }}>
                                                                {(q.asker_first_name || "?")[0].toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                                                <span className="text-xs font-semibold text-gray-700">
                                                                    {q.asker_first_name} {q.asker_last_name}
                                                                </span>
                                                                {q.campaign_name && (
                                                                    <span className="text-xs text-gray-400">· {q.campaign_name}</span>
                                                                )}
                                                                {!q.answer ? (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Pending</span>
                                                                ) : (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Answered</span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm font-medium text-gray-900 leading-snug">{q.question}</p>
                                                            {q.answer && (
                                                                <p className="text-sm text-gray-500 mt-1.5 pl-3 border-l-2 border-[#C2D7E2] leading-relaxed">{q.answer}</p>
                                                            )}
                                                            {!q.answer && (
                                                                <button
                                                                    onClick={() => navigate("/entrepreneur-questions")}
                                                                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                                                                    style={{ color: AQI_PRIMARY }}
                                                                >
                                                                    Answer this question <ArrowUpRight className="h-3 w-3" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {campaignQuestions.length > 8 && (
                                                <div className="px-6 py-4 text-center">
                                                    <button
                                                        onClick={() => navigate("/entrepreneur-questions")}
                                                        className="text-sm font-semibold hover:underline"
                                                        style={{ color: AQI_PRIMARY }}
                                                    >
                                                        View all {campaignQuestions.length} questions →
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Campaign Updates */}
                                <CampaignUpdatesBlock
                                    campaignUpdates={campaignUpdates}
                                    renderMedia={renderMedia}
                                    onExpand={setExpandedCampaignIndex}
                                />
                            </>
                        )}
                    </section>
                )}

            {/* ── Modal: View all updates ───────────────────────────────────── */}
            {expandedCampaignIndex !== null && campaignUpdates[expandedCampaignIndex] && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
                    onClick={() => setExpandedCampaignIndex(null)}
                >
                    <div
                        className="bg-white rounded-2xl p-6 w-11/12 max-w-5xl max-h-[80vh] overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <FaChartLine style={{ color: AQI_PRIMARY }} />
                                {campaignUpdates[expandedCampaignIndex].name || "Campaign Updates"}
                            </h2>
                            <button
                                onClick={() => setExpandedCampaignIndex(null)}
                                className="text-gray-400 hover:text-gray-700 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="overflow-y-auto flex-grow pr-2" style={{ scrollbarWidth: "thin", scrollbarColor: "#C2D7E2 #f1f1f1" }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {campaignUpdates[expandedCampaignIndex].updates.map((update, i) => {
                                    const isObj = typeof update === "object" && update !== null;
                                    const timestamp =
                                        isObj && update.timestamp
                                            ? new Date(update.timestamp).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                            })
                                            : null;
                                    const title = isObj ? update.title : null;
                                    const text = isObj ? update.description || update.text : typeof update === "string" ? update : null;
                                    const url = isObj ? update.url : null;
                                    const media = isObj ? update.media : null;
                                    return (
                                        <div
                                            key={i}
                                            className="rounded-xl bg-[#F5F7FA] overflow-hidden"
                                            style={{ border: "1px solid #e3eaf3", borderLeft: "4px solid var(--navy)" }}
                                        >
                                            <div className="p-4">
                                                {timestamp && (
                                                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#e8f1f8] text-navy mb-3">
                                                        <FaClock className="h-2.5 w-2.5" />
                                                        {timestamp}
                                                    </span>
                                                )}
                                                {title && (
                                                    <p className="text-sm font-semibold text-navy leading-snug mb-2 break-words">{title}</p>
                                                )}
                                                {url && (
                                                    <a
                                                        href={normalizeExternalUrl(url)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="mb-2 inline-flex w-fit max-w-full items-center gap-1.5 rounded-full border border-navy/20 bg-[#f0f6fb] px-3 py-1 text-sm font-semibold text-navy transition hover:border-navy/40 hover:bg-[#e3eef8]"
                                                    >
                                                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                                        <span className="truncate">{getLinkLabel(url)}</span>
                                                    </a>
                                                )}
                                                {text && (
                                                    <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
                                                )}
                                                {media && (
                                                    <div className="mt-3 rounded-lg overflow-hidden border border-[#e3eaf3] bg-white">
                                                        {renderMedia(media)}
                                                    </div>
                                                )}
                                                {!title && !text && !url && !media && (
                                                    <p className="text-sm text-gray-400 italic">No content</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

EntrepreneurDashboard.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string,
        role: PropTypes.string,
    }),
};
export default EntrepreneurDashboard;
